import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Box, Grid, styled } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import Cropper, { Area } from 'react-easy-crop';

import { FileTypeVariants } from '../../components/WsFileUpload';
import WsColors from '../../WsColors';
import { WsButton } from '../WsButton';
import WsFileUploadIcon, { IconVariant } from '../WsIcon/FileUploadIcon';
import { WsTypoColors, WsTypography, WsTypoVariants } from '../WsTypography';

const StyledBox = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: 2,
  borderWidth: 2,
  borderRadius: 2,
  borderColor: WsColors.BorderGrey,
  borderStyle: 'solid',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  position: 'relative',
  textAlign: 'center',
});

const CropContainer = styled(Grid)({
  position: 'relative',
  width: '100%',
  height: 400,
  background: WsColors.GreyBlack,
});

const CroppedImagePreview = styled('img')({
  maxWidth: '100%',
  borderRadius: 8,
});

const PaddedText = styled(Box)({
  padding: 8,
});

export interface IFileUploadProps {
  onUpload?: (files: File[]) => void;
  placeholderText: string;
  placeholderHelperText?: string;
  helperText?: string;
  onFileRejected?: (fileRejections: FileRejection[]) => void;
  maxFileSize?: number;
  validationMessage?: string;
  disabled?: boolean;
  handleCrop: (imgString: string) => void;
}
interface MessageState {
  text: string;
  color: WsTypoColors;
  showHelper: boolean;
}

const readFile = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

const validateFileSize = (file: File, maxFileSize: number): boolean => {
  const fileSizeInMB = file.size / (1024 * 1024);
  return fileSizeInMB <= maxFileSize;
};

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (imageSrc: string, crop: Area): Promise<string> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  canvas.width = crop.width;
  canvas.height = crop.height;
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
  return canvas.toDataURL('image/jpeg');
};

const WsProfileImageSelector = ({
  onUpload,
  placeholderText = "Drag 'n' drop Image, or click to select",
  helperText = 'Drop your Image here ...',
  validationMessage = 'File is not in correct format or exceeds Size Limit 5 MB',
  onFileRejected,
  maxFileSize = 5,
  placeholderHelperText = '',
  disabled = false,
  handleCrop,
}: IFileUploadProps) => {
  const [fileSizeError, setFileSizeError] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [fileError, setFileError] = useState<string | null>(null);
  const {
    getRootProps: getDocumentRootProps,
    getInputProps: getDocumentInputProps,
    isDragActive: isDocumentDragActive,
    isDragReject,
    isDragAccept,
  } = useDropzone({
    onDropAccepted: onUpload,
    maxSize: maxFileSize * 1024 * 1024,
    onDropRejected: onFileRejected,
    accept: FileTypeVariants.IMAGES,
  });
  const getMessageState = (): MessageState => {
    const errorConditions = [
      { condition: isDragReject && !isDragAccept, message: validationMessage },
      { condition: fileSizeError, message: validationMessage },
      { condition: fileError, message: fileError },
    ];

    const activeError = errorConditions.find((error) => error.condition);

    if (activeError && activeError.message) {
      return {
        text: activeError.message,
        color: WsTypoColors.ERROR,
        showHelper: false,
      };
    }

    return {
      text: isDocumentDragActive ? helperText : placeholderText,
      color: WsTypoColors.SECONDARY,
      showHelper: true,
    };
  };

  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const files = Array.from(event.target.files || []);
      const file = files[0];
      if (!file) return;
      const isValidSize = validateFileSize(file, maxFileSize);
      setFileSizeError(!isValidSize);
      if (!isValidSize) return;
      const imageDataUrl = await readFile(file);
      setImageSrc(imageDataUrl);
      setFileError(null);
    } catch (error) {
      setFileError('Failed to read file. Please try again.');
      console.error('File reading error:', error);
    }
  };

  const cropImage = async (src: string, pixels: Area) => {
    try {
      const croppedBase64 = await getCroppedImg(src, pixels);
      setCroppedImage(croppedBase64);
      handleCrop(croppedBase64);
    } catch (error) {
      setFileError('Failed to crop image. Please try again.');
      console.error('Cropping error:', error);
    }
  };

  const onCropComplete = useCallback(async (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
    if (!imageSrc || !croppedAreaPixels) return;
    await cropImage(imageSrc, croppedAreaPixels);
  }, []);

  const handleCropClick = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    await cropImage(imageSrc, croppedAreaPixels);
  };

  const renderUploadBox = () => (
    <StyledBox {...getDocumentRootProps()} sx={{ bgcolor: WsColors.White }}>
      <WsFileUploadIcon variant={IconVariant.APP} />
      <input
        accept={FileTypeVariants.IMAGES}
        {...getDocumentInputProps({ onChange: handleFileSelection })}
      />
      <PaddedText>
        <WsTypography color={getMessageState().color} variant={WsTypoVariants.TITLE}>
          {getMessageState().text}
        </WsTypography>
      </PaddedText>
      {getMessageState().showHelper && placeholderHelperText && (
        <PaddedText>
          <WsTypography color={WsTypoColors.SECONDARY} variant={WsTypoVariants.BODY1}>
            {placeholderHelperText}
          </WsTypography>
        </PaddedText>
      )}
      <WsButton disabled={disabled} startIcon={<FileUploadOutlinedIcon />} variant="outlined">
        Upload image
      </WsButton>
    </StyledBox>
  );

  const renderCropper = () => (
    <Grid container>
      <CropContainer item xs={12}>
        <Cropper
          image={imageSrc!}
          crop={crop}
          zoom={zoom}
          aspect={4 / 4}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          cropShape="rect"
          showGrid={true}
        />
      </CropContainer>
      <Grid item xs={12} display="flex" justifyContent="flex-end" sx={{ m: 2 }}>
        <WsButton onClick={handleCropClick}>Crop</WsButton>
      </Grid>
    </Grid>
  );

  const renderCroppedImage = () => (
    <Box mt={2} textAlign="center">
      <CroppedImagePreview src={croppedImage!} alt="Cropped Preview" />
    </Box>
  );
  if (imageSrc && !croppedImage) return renderCropper();

  if (croppedImage) return renderCroppedImage();

  return renderUploadBox();
};

export default WsProfileImageSelector;
